// WS5 — brokerage duplicate-check modal, cloned from the vendor
// VendorDedupModal (which itself mirrors the counterparty DedupCheckModal's
// layout: strength badges, candidate rows, create-anyway override). A separate
// component because DedupCheckModal's props are counterparty-bound (kind +
// self-run dedup fetch); the brokerage flow already HAS the result — it rides
// the create endpoint's 409 body — so this modal is purely presentational.

import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Badge, Button, FormField } from "@/design-system/components/ui";
import { Textarea } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { cn } from "@/design-system/lib/utils";
import type {
  DedupCandidateDto,
  DedupCheckResultDto,
  DedupMatchStrength,
} from "@/types/counterparties";

const STRENGTH_BADGE: Record<
  DedupMatchStrength,
  "error" | "review" | "info" | "finalized"
> = {
  Strong: "error",
  Likely: "review",
  Possible: "info",
  None: "finalized",
};

const STRENGTH_LABEL: Record<DedupMatchStrength, string> = {
  Strong: "Strong match",
  Likely: "Likely match",
  Possible: "Possible match",
  None: "No match",
};

export interface BrokerageDedupModalProps {
  open: boolean;
  result: DedupCheckResultDto;
  /** "Use existing" — the parent navigates to the candidate's detail page. */
  onUseExisting: (candidate: DedupCandidateDto) => void;
  /** "Create anyway" — the parent resubmits with confirmCreateNew: true.
   *  The reason is optional; an empty string means none was captured. */
  onCreateAnyway: (reason: string) => void;
  onCancel: () => void;
  pending?: boolean;
}

export function BrokerageDedupModal({
  open,
  result,
  onUseExisting,
  onCreateAnyway,
  onCancel,
  pending = false,
}: BrokerageDedupModalProps) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onCancel}
      dismissOnBackdropClick={false}
      title="Duplicate Check"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => onCreateAnyway(reason.trim())}
            disabled={pending}
            data-testid="brokerage-dedup-create-anyway"
          >
            Create anyway
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-4">
        <div className="flex items-start gap-space-2 text-small text-status-error">
          <AlertTriangle
            size={16}
            strokeWidth={1.5}
            aria-hidden
            className="mt-[2px]"
          />
          <div>
            <p className="font-semibold m-0">
              This brokerage looks like an existing record.
            </p>
            <p className="m-0 text-gray-dark">
              Phoenix recommends using the existing brokerage. To create a new
              record anyway, you can explain why below.
            </p>
          </div>
        </div>

        {result.candidates.length > 0 && (
          <ul className="flex flex-col divide-y divide-border-light border border-border-light rounded-md">
            {result.candidates.map((c) => (
              <li
                key={c.id}
                data-testid={`brokerage-dedup-candidate-${c.id}`}
                className={cn(
                  "flex items-center justify-between gap-space-3 p-space-3",
                  c.matchStrength === "Strong" && "bg-status-error/5",
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-space-2">
                    <Badge status={STRENGTH_BADGE[c.matchStrength]}>
                      {STRENGTH_LABEL[c.matchStrength]}
                    </Badge>
                    <span className="font-semibold text-small text-onyx truncate">
                      {c.displayName}
                    </span>
                  </div>
                  <p className="text-caption text-gray-mid mt-space-1">
                    {c.matchReason}
                  </p>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => onUseExisting(c)}
                >
                  Use existing
                </Button>
              </li>
            ))}
          </ul>
        )}

        <FormField
          label="Reason for creating a new brokerage (optional)"
          helperText="Saved to the audit log with the override."
        >
          <Textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Different regional office — same brand, separate firm."
            data-testid="brokerage-dedup-reason"
          />
        </FormField>
      </div>
    </Modal>
  );
}
